import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonalRecord } from './api';

interface RecordsTableProps {
    records: PersonalRecord[];
    deletingId: string | null;
    onEdit: (rec: PersonalRecord) => void;
    onDelete: (id: string) => void;
}

const RecordsTable: React.FC<RecordsTableProps> = ({ records, deletingId, onEdit, onDelete }) => {
    const { t } = useTranslation();
    return (
    <>
    {/* Mobile cards. Five columns of px-4 cells cannot fit a phone, and the
        overflow-x wrapper below just turns that into a nested sideways
        scroller. The table is unchanged and still owns every md+ width. */}
    <div className="md:hidden divide-y divide-cyan-100">
        {records.length === 0 ? (
            <p className="px-1 py-6 text-center text-slate-400 text-xs italic">
                {t('personalTest.emptyRecords')}
            </p>
        ) : (
            records.map((rec) => (
                <div key={rec.id} className="py-3 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                            <span className="font-bold text-slate-700 truncate">{rec.distance}</span>
                            <span className="font-mono text-sm text-cyan-700 flex-shrink-0">{rec.timeValue}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{rec.achievedAt}</div>
                        {rec.notes && (
                            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{rec.notes}</div>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={() => onEdit(rec)}
                            aria-label={t('common.edit')}
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 active:bg-slate-100"
                        >
                            <i className="fa-solid fa-pen text-sm"></i>
                        </button>
                        <button
                            onClick={() => onDelete(rec.id)}
                            disabled={deletingId === rec.id}
                            aria-label={t('common.delete')}
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 active:bg-red-50 disabled:opacity-50"
                        >
                            {deletingId === rec.id
                                ? <i className="fa-solid fa-spinner fa-spin text-sm"></i>
                                : <i className="fa-solid fa-trash text-sm"></i>}
                        </button>
                    </div>
                </div>
            ))
        )}
    </div>

    <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm text-left">
            <thead className="bg-cyan-50 text-cyan-700 uppercase font-bold text-xs">
                <tr>
                    <th className="px-4 py-3 rounded-l-lg">{t('personalTest.colDistance')}</th>
                    <th className="px-4 py-3">{t('personalTest.colTime')}</th>
                    <th className="px-4 py-3">{t('common.date')}</th>
                    <th className="px-4 py-3">{t('common.notes')}</th>
                    <th className="px-4 py-3 rounded-r-lg w-8"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-cyan-100">
                {records.length === 0 ? (
                    <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-slate-400 text-xs italic">
                            {t('personalTest.emptyRecords')}
                        </td>
                    </tr>
                ) : (
                    records.map((rec) => (
                        <tr key={rec.id} className="hover:bg-cyan-50 transition-colors group">
                            <td className="px-4 py-3 font-bold text-slate-700">{rec.distance}</td>
                            <td className="px-4 py-3 font-mono text-slate-700">{rec.timeValue}</td>
                            <td className="px-4 py-3 text-slate-700">{rec.achievedAt}</td>
                            <td className="px-4 py-3 text-slate-700">{rec.notes || '—'}</td>
                            <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => onEdit(rec)}
                                            title={t('common.edit')}
                                            className="text-slate-300 hover:text-blue-500"
                                        >
                                            <i className="fa-solid fa-pen text-xs"></i>
                                        </button>
                                        <button
                                            onClick={() => onDelete(rec.id)}
                                            disabled={deletingId === rec.id}
                                            title={t('common.delete')}
                                            className="text-slate-300 hover:text-red-500 disabled:opacity-50"
                                        >
                                            {deletingId === rec.id
                                                ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                                : <i className="fa-solid fa-trash text-xs"></i>}
                                        </button>
                                    </div>
                                </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
    </>
    );
};

export default RecordsTable;
